import React, { useState } from "react";
import OpenAI from "openai";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
const AIAnalyzer = () => {
  const [codeSnippet, setCodeSnippet] = useState("");
  const [updatedCodeSnippet, setUpdatedCodeSnippet] = useState("");
  const [result, setResult] = useState([]);
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navi = useNavigate();

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const filetypes = [
        ".js",
        ".html",
        ".css",
        ".php",
        ".py",
        ".java",
        ".c",
        ".cpp",
        ".cs",
        ".rb",
        ".go",
        ".ts",
        ".txt",
      ];
      if (
        file.type === "text/plain" ||
        filetypes.includes(file.name.slice(-3))
      ) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setCodeSnippet(e.target.result);
        };
        reader.readAsText(file);
      } else {
        alert("Please upload a text file.");
      }
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCodeSnippet(e.target.result);
      };
      reader.readAsText(file);
    } else {
      alert("Please drop a text file.");
    }
  };
  const exportToPDF = () => {
    const doc = new jsPDF();

    // Set up initial formatting
    doc.setFont("helvetica");
    doc.setFontSize(22);
    doc.setTextColor(255, 69, 0); // Set text color to orange
    doc.text("Vulnerability Report", 105, 20, { align: "center" });

    // Add date
    const currentDate = new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    doc.setTextColor(0); // Reset text color to black
    doc.setFontSize(12);
    doc.text(`Date: ${currentDate}`, 10, 30);

    // Add vulnerabilities
    let y = 40;
    result.forEach((vulnerability, index) => {
      const parts = vulnerability.split("$");
      let title, description;
      if (parts.length === 2) {
        title = parts[0].trim();
        description = parts[1];
      } else {
        return;
      }

      // Check if there's enough space on the page for the vulnerability
      const lineHeight = doc.getLineHeight();
      const lines = doc.splitTextToSize(description, 180);
      const requiredHeight = (lines.length + 2) * lineHeight; // Title + Description + Spacing
      if (y + requiredHeight > doc.internal.pageSize.height - 10) {
        doc.addPage(); // Add a new page if there's not enough space
        y = 10; // Reset y position
      }

      // Add title
      doc.setFillColor(240, 240, 240); // Light gray background for titles
      doc.rect(10, y, 190, 10, "F");
      doc.setTextColor(0, 0, 255); // Set text color to blue for titles
      doc.setFontSize(14);
      doc.text(`${title}:`, 15, y + 7);
      y += 12;

      // Add description
      doc.setTextColor(0); // Reset text color to black
      doc.setFontSize(12);
      lines.forEach((line) => {
        doc.text(line, 15, y);
        y += lineHeight; // Adjust line spacing
      });

      y += 5; // Add spacing between vulnerabilities
    });

    // Save the PDF
    doc.save("vulnerability_report.pdf");
  };

  const analyzeCode = async () => {
    setIsLoading(true);
    const openai = new OpenAI({
      apiKey: "sk-proj-UGo4yWRClUpmGlIKAzbQT3BlbkFJYKSHkoJdcAMMMtktZGbs",
      dangerouslyAllowBrowser: true,
    });
    try {
      if (!codeSnippet.trim()) {
        throw new Error("Code snippet is empty.");
      }

      setResult([]);
      setDetails(null);
      const completion = await openai.chat.completions.create({
        messages: [
          {
            role: "system",
            content:
              "You will act as a web vulnerability and security expert. Analyze the following code and list each web vulnerability you find. Prioritize the OWASP Top Ten vulnerabilities from 2022. List each vulnerability seperated by a '~'. After the ~, have the title of the vulnerability, followed by a $. The title should be 1 or 2 words with no symbols or special characters that describes the issue. Then include a 2-3 sentence explaination of the vulnerability. Do not include any text in your response that does not start with a ~ or that isnt a vulnerability. Only show vulnerabilities that are not protected in the code given. Do not return any vulnerabilities that have had been patched. If the code provided   Here is the code:",
          },
          { role: "user", content: codeSnippet },
        ],
        model: "gpt-4-turbo",
      });
      setResult(completion.choices[0].message.content.split("~").slice(1));
      //   const longStr =
      //     "~Cryptographic Failures$ The example uses plain text passwords in the database query, which lacks proper cryptographic practices for handling sensitive data. Storing or transmitting passwords in plain text can lead to their disclosure and misuse if an attacker gains access to the database or intercepts the data.~Security Misconfiguration$ This code lacks measures to protect against brute force attacks. By not limiting failed login attempts, it allows attackers to repeatedly try different usernames and passwords until they find a correct combination, increasing the risk of unauthorized access.~Broken Authentication$ Sessions are poorly managed in that the application does not regenerate session IDs upon successful login. This decision makes the application more susceptible to session fixation attacks, where an attacker can hijack a user's session after they authenticate.";
      //   setResult(longStr.split("~").slice(1));
    } catch (error) {
      console.error("Failed to analyze code.", error);
      setResult(["Failed to analyze code. Please try again"]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVulnerabilityClick = async (title) => {
    setIsLoading(true);
    const openai = new OpenAI({
      apiKey: "sk-proj-UGo4yWRClUpmGlIKAzbQT3BlbkFJYKSHkoJdcAMMMtktZGbs",
      dangerouslyAllowBrowser: true,
    });
    try {
      const completion = await openai.chat.completions.create({
        messages: [
          {
            role: "system",
            content:
              "Provide a somewhat detailed analysis of the following vulnerability, given its occurrences in the code. Provide the best code solution available to solve this vulnerability. Only provide 1 solution. Keep the total response lower than 150 words. The vulnerability and code follows:",
          },
          { role: "system", content: title },
          { role: "user", content: codeSnippet },
        ],
        model: "gpt-4-turbo",
      });

      const responseText = completion.choices[0].message.content;
      const regex = /```([\s\S]*?)```/g;
      const matches = [...responseText.matchAll(regex)];

      let newUpdatedCodeSnippet = "";
      if (matches.length > 0 && matches[0].length > 0) {
        newUpdatedCodeSnippet = matches[0][1]; // Capture the code inside the backticks
      }

      setUpdatedCodeSnippet(newUpdatedCodeSnippet);
      setDetails(responseText);

      // Ensure state updates are processed before navigating
      setTimeout(() => {
        navi("/Vulnerability", {
          state: {
            title: title,
            description: responseText,
            codeSnippet: codeSnippet,
            updatedCodeSnippet: newUpdatedCodeSnippet,
          },
        });
      }, 0);
    } catch (error) {
      console.error("Failed to get details for the vulnerability:", error);
      alert(
        "Failed to get details for the vulnerability. Please check the console for more information."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <textarea
        className="monospace"
        class="monospace"
        value={codeSnippet}
        onChange={(e) => setCodeSnippet(e.target.value)}
        placeholder="Enter code snippet here / Drop a file here..."
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
      <br />
      <input type="file" onChange={handleFileChange} />
      <button class="orangeBtn realbtn" onClick={analyzeCode}>
        Analyze Code
      </button>
      <br />
      <br />
      <button onClick={exportToPDF} disabled={!result.length}>
        Export PDF
      </button>
      {isLoading && <div className="spinner"></div>}
      {!isLoading && result && (
        <div>
          {result.map((vuln, index) => {
            const parts = vuln.split("$");
            let display = true;
            let title, description;
            if (parts[0]) {
              title = parts[0].trim();
            } else {
              display = false;
            }

            if (parts[1]) {
              description = parts[1];
            } else {
              display = false;
            }

            if (display) {
              return (
                <div key={index}>
                  <p className="ResultOutput">
                    <strong>{title}:</strong> {description}
                  </p>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleVulnerabilityClick(title);
                    }}
                    class="orangeBtn realbtn"
                  >
                    {isLoading ? (
                      <div className="button-spinner"></div>
                    ) : (
                      "Learn More"
                    )}
                  </button>
                  <p>{details}</p>
                </div>
              );
            } else {
              return "";
            }
          })}
        </div>
      )}
    </div>
  );
};

export default AIAnalyzer;
